import { useState, useEffect } from 'react';

/**
 * Auth session stored in localStorage.
 *
 * The JWT itself lives in an HttpOnly cookie set by the backend — it is never
 * accessible to JavaScript. This store holds only user-display data
 * (name, email, roles) plus an expiry hint so the UI can proactively show
 * the login screen when the cookie has expired.
 */
const STORAGE_KEY_PREFIX = 'ota.auth';

export interface AuthSession {
  /** Mirrors the cookie TTL so the UI can detect expiry without an API round-trip. */
  expiresAt: number;
  user: {
    id: number;
    email: string;
    fullName?: string;
    phone?: string;
    roles: string[];
  };
}

export interface AuthStore {
  get(): AuthSession | null;
  set(session: AuthSession): void;
  clear(): void;
  subscribe(listener: (s: AuthSession | null) => void): () => void;
}

export function createAuthStore(appId: 'public' | 'internal' | 'admin' = 'public'): AuthStore {
  const STORAGE_KEY = `${STORAGE_KEY_PREFIX}.${appId}`;
  let listeners: Array<(s: AuthSession | null) => void> = [];

  const read = (): AuthSession | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const session = JSON.parse(raw) as AuthSession;
      if (session.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return session;
    } catch {
      return null;
    }
  };

  const emit = (s: AuthSession | null) => listeners.forEach((l) => l(s));

  return {
    get: read,
    set(session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      emit(session);
    },
    clear() {
      localStorage.removeItem(STORAGE_KEY);
      emit(null);
    },
    subscribe(listener) {
      listeners.push(listener);
      return () => {
        listeners = listeners.filter((l) => l !== listener);
      };
    },
  };
}

/**
 * Reactive hook — re-renders the component whenever the auth session changes
 * (login, logout, or expiry). Use this instead of calling {@code authStore.get()}
 * directly in components that need to react to auth state changes.
 *
 * @example
 * const session = useAuthSession(authStore);
 * if (!session) return <LoginPrompt />;
 */
export function useAuthSession(store: AuthStore): AuthSession | null {
  const [session, setSession] = useState<AuthSession | null>(() => store.get());
  useEffect(() => store.subscribe(setSession), [store]);
  return session;
}
